import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Check, Sparkles, Video, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const VOICE_LIST = [
  "2e4e9689be0f4a05aa5fae4e36e025c4","Warm Pro Narrator","Antoni","Matt","Archie - Casual","Tim in car - Excited","Santa","Baritone Ben","Dr. Grey - Knowledgeable","Santa Chris","Sophie - Cheerful","Masha - Lifelike","Feisty Fiona - Excited","Josh","Zoey - Hushed","Chloe - Lifelike","George UGC 1","Daniel","Vanessa","Kate - Professional","Lida - ecommerce 1","Professor Dean","Joanne - UGC","Chris - Friendly","June - Lifelike","Andrew - Natural Narrator","Cassidy","Ezra - Motivational","Cute Chloe - Friendly","Rose - UGC","Alice - Confident","Marcus - Natural Storyteller","Kayla","Melissa","Brenda - UGC","Edward","Valentino","Tyler - Informative","Amber","Sara","Nathan","Lucas","Tony","Jake","Dave - Conversational","Ashley","Mike","Eliza","Mark - Natural conversational","Julie","Grace","Lily","Liam - Friendly","Victoria","Kira","Dexter","Amanda - Friendly","Elena","Jenna","Oliver","Derek","Monica","Sophia","Cameron","Ethan","Rachel","Henry","Leah","Max - Fun","Harper","Ryan - Sports Commentator","Tanya - Helpful","Ivy","Mason","Brooklyn","Nolan"
];

const TALKING_STYLES = [
  { value: "expressive", label: "Expressive", desc: "Animated, lively — great for UGC/marketing" },
  { value: "stable", label: "Stable", desc: "Calm, minimal movement — formal/informational" },
];

export default function UGCAvatar4Creator({ template, setTemplate, onContinue }) {
  const [voice, setVoice] = useState(template?.voice || "");
  const [talkingStyle, setTalkingStyle] = useState(template?.talking_style || "expressive");
  const [script, setScript] = useState(template?.script || "");
  const [loading, setLoading] = useState(false);
  const [voiceSearch, setVoiceSearch] = useState("");

  useEffect(() => {
    if (template) {
      if (template.voice) setVoice(template.voice);
      if (template.talking_style) setTalkingStyle(template.talking_style);
      if (template.script) setScript(template.script);
    }
  }, [template]);

  const filteredVoices = VOICE_LIST.filter(v =>
    v.toLowerCase().includes(voiceSearch.toLowerCase())
  );

  const saveVoiceAndScript = async () => {
    if (!template) return;
    if (!voice) { toast.error("Please select a voice"); return; }
    if (!script.trim()) { toast.error("Please enter a script"); return; }

    setLoading(true);
    try {
      // Save script
      const formData = new FormData();
      formData.append("script", script);
      await axios.post(`${API}/api/templates/${template.id}/save-script`, formData, { withCredentials: true });

      // Save voice, talking_style, and video_method
      await axios.put(`${API}/api/templates/${template.id}`, {
        voice,
        talking_style: talkingStyle,
      }, { withCredentials: true });

      setTemplate({ ...template, script, voice, talking_style: talkingStyle });
      toast.success("Voice & script saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const generateVideo = async (sceneIndex) => {
    if (!template) return;
    setLoading(true);
    try {
      toast.info("Starting Avatar4 video generation...");
      const formData = new FormData();
      formData.append("scene_index", sceneIndex);
      formData.append("video_method", "fal_avatar4");
      const resp = await axios.post(
        `${API}/api/templates/${template.id}/generate-scene-video`,
        formData,
        { withCredentials: true }
      );
      const jobId = resp.data.job_id;

      // Poll for completion
      const poll = async () => {
        const status = await axios.get(
          `${API}/api/templates/${template.id}/scene-video-job/${jobId}`,
          { withCredentials: true }
        );
        if (status.data.status === "completed") {
          toast.success("Avatar4 video generated!");
          // Refresh template
          const t = await axios.get(`${API}/api/templates/${template.id}`, { withCredentials: true });
          setTemplate(t.data);
          setLoading(false);
          if (onContinue) onContinue();
        } else if (status.data.status === "failed") {
          toast.error(status.data.error || "Video generation failed");
          setLoading(false);
        } else {
          setTimeout(poll, 5000);
        }
      };
      poll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start video generation");
      setLoading(false);
    }
  };

  const sceneVideos = template?.scene_videos || [];
  const hasVideo = sceneVideos.length > 0 && sceneVideos[0];

  return (
    <div className="space-y-6" data-testid="ugc-avatar4-creator">
      {/* Voice Selection */}
      <div>
        <Label className="text-base font-medium">Voice</Label>
        <p className="text-sm text-muted-foreground mb-2">
          Select the voice for the avatar. This voice will be used for all recreations.
        </p>
        <div className="relative mb-2">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            value={voiceSearch}
            onChange={(e) => setVoiceSearch(e.target.value)}
            placeholder="Search voices..."
            className="pl-9"
            data-testid="voice-search-input"
          />
        </div>
        <div className="max-h-48 overflow-y-auto border rounded-lg p-1 space-y-0.5" data-testid="voice-list">
          {filteredVoices.map((v) => (
            <button
              key={v}
              onClick={() => setVoice(v)}
              className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                voice === v
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent"
              }`}
              data-testid={`voice-option-${v.replace(/\s/g, '-')}`}
            >
              {v}
              {voice === v && <Check className="w-3 h-3 inline ml-2" />}
            </button>
          ))}
          {filteredVoices.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-3">No voices found</p>
          )}
        </div>
        {voice && (
          <p className="text-sm mt-2 text-primary font-medium" data-testid="selected-voice">
            Selected: {voice}
          </p>
        )}
      </div>

      {/* Talking Style */}
      <div>
        <Label className="text-base font-medium">Talking Style</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {TALKING_STYLES.map((style) => (
            <button
              key={style.value}
              onClick={() => setTalkingStyle(style.value)}
              className={`p-3 rounded-lg border text-left transition-all ${
                talkingStyle === style.value
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "hover:border-primary/50"
              }`}
              data-testid={`talking-style-${style.value}`}
            >
              <p className="text-sm font-medium">{style.label}</p>
              <p className="text-xs text-muted-foreground">{style.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Script */}
      <div>
        <Label className="text-base font-medium">Script</Label>
        <p className="text-sm text-muted-foreground mb-2">
          Write what the avatar will say. This text is sent directly to HeyGen's TTS.
        </p>
        <Textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          placeholder="Hey everyone! Check out this amazing product..."
          className="min-h-[100px]"
          data-testid="avatar4-script-textarea"
        />
        <p className="text-xs text-muted-foreground mt-1">
          {script.trim().split(/\s+/).filter(w => w).length} words
        </p>
      </div>

      {/* Save Button */}
      <Button
        onClick={saveVoiceAndScript}
        disabled={loading || !voice || !script.trim()}
        className="w-full"
        data-testid="save-voice-script-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
        {template?.script && template?.voice ? "Update Voice & Script" : "Save Voice & Script"}
      </Button>

      {/* Generate Video */}
      {template?.script && template?.voice && template?.composite_image && (
        <div className="pt-4 border-t space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">Generate Video</Label>
            {hasVideo && <span className="text-xs text-green-600 font-medium">Video generated</span>}
          </div>

          {template?.composite_image && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Composite image (input for video)</p>
              <img src={template.composite_image} alt="Composite" className="max-h-40 mx-auto rounded-lg" />
            </div>
          )}

          {hasVideo && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Generated video</p>
              <video src={sceneVideos[0]} controls className="max-h-48 mx-auto rounded-lg" />
            </div>
          )}

          <Button
            onClick={() => generateVideo(0)}
            disabled={loading}
            className="w-full"
            data-testid="generate-avatar4-video-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
            ) : (
              <><Video className="w-4 h-4 mr-2" />{hasVideo ? "Regenerate Video" : "Generate Avatar4 Video"}</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
